// realTimePriceService.ts - Real-time price updates from Polygon API
import { ScannerData } from '../types/ScannerData';

interface PolygonQuote {
  c: number; // Close price
  h: number; // High price
  l: number; // Low price
  o: number; // Open price
  v: number; // Volume
  vw: number; // Volume weighted average price
  t: number; // Timestamp
}

interface PolygonResponse {
  results: PolygonQuote[];
  status: string;
  request_id: string;
  next_url?: string;
}

class RealTimePriceService {
  private polygonApiKey: string;
  private priceCache: Map<string, PolygonQuote> = new Map();
  private updateInterval: NodeJS.Timeout | null = null;
  private subscribers: Set<(prices: Map<string, PolygonQuote>) => void> = new Set();
  private isRunning: boolean = false;

  constructor() {
    // Get Polygon API key from environment or use default
    this.polygonApiKey = process.env.REACT_APP_POLYGON_API_KEY || 'your_polygon_api_key_here';
  }

  // Start real-time price updates
  start(underlyingSymbols: string[], intervalMs: number = 1000) {
    if (this.isRunning) {
      this.stop();
    }

    this.isRunning = true;
    console.log('🔄 [REAL-TIME] Starting price updates for symbols:', underlyingSymbols);

    // Update prices immediately
    this.updatePrices(underlyingSymbols);

    // Set up interval for regular updates
    this.updateInterval = setInterval(() => {
      this.updatePrices(underlyingSymbols);
    }, intervalMs);
  }

  // Stop real-time updates
  stop() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
    this.isRunning = false;
    console.log('⏹️ [REAL-TIME] Stopped price updates');
  }

  // Subscribe to price updates
  subscribe(callback: (prices: Map<string, PolygonQuote>) => void) {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  }

  // Get cached price for a symbol
  getPrice(symbol: string): PolygonQuote | null {
    return this.priceCache.get(symbol) || null;
  }

  // Update prices from Polygon API
  private async updatePrices(symbols: string[]) {
    try {
      const promises = symbols.map(symbol => this.fetchSymbolPrice(symbol));
      await Promise.all(promises);
      
      // Notify subscribers
      this.subscribers.forEach(callback => callback(this.priceCache));
    } catch (error) {
      console.error('❌ [REAL-TIME] Error updating prices:', error);
    }
  }

  // Fetch price for a single symbol
  private async fetchSymbolPrice(symbol: string) {
    try {
      // Convert symbol to Polygon format
      const polygonSymbol = this.convertToPolygonSymbol(symbol);
      
      const url = `https://api.polygon.io/v2/aggs/ticker/${polygonSymbol}/prev?adjusted=true&apikey=${this.polygonApiKey}`;
      
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: PolygonResponse = await response.json();
      
      if (data.results && data.results.length > 0) {
        const quote = data.results[0];
        this.priceCache.set(symbol, quote);
        console.log(`📈 [REAL-TIME] Updated ${symbol}: $${quote.c}`);
      }
    } catch (error) {
      console.error(`❌ [REAL-TIME] Error fetching price for ${symbol}:`, error);
    }
  }

  // Convert our symbol format to Polygon format
  private convertToPolygonSymbol(symbol: string): string {
    // Handle different symbol formats
    if (symbol === 'SPY') return 'SPY';
    if (symbol === 'QQQ') return 'QQQ';
    if (symbol === 'GLD') return 'GLD';
    if (symbol === '$SPXW.X') return 'SPX';
    if (symbol === 'SPX') return 'SPX';
    
    // Default to symbol as-is
    return symbol;
  }

  // Update ScannerData with real-time prices
  updateScannerDataWithPrices(data: ScannerData[]): ScannerData[] {
    return data.map(record => {
      const underlying = record.Underlying;
      const priceData = this.getPrice(underlying);
      
      if (priceData) {
        return {
          ...record,
          'Underlying Price': priceData.c, // Use close price as current price
          'Last': priceData.c, // Update last price
          'Mid': priceData.c, // Update mid price
          // Add additional price data
          'Open Price': priceData.o,
          'High Price': priceData.h,
          'Low Price': priceData.l,
          'Volume Weighted Price': priceData.vw,
          'Price Timestamp': new Date(priceData.t).toISOString()
        };
      }
      
      return record;
    });
  }

  // Get all cached prices
  getAllPrices(): Map<string, PolygonQuote> {
    return new Map(this.priceCache);
  }

  // Check if service is running
  isServiceRunning(): boolean {
    return this.isRunning;
  }
}

// Export singleton instance
export const realTimePriceService = new RealTimePriceService();

// Export the class for testing
export { RealTimePriceService };
